import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface DetailPageLayoutProps {
  children: ReactNode;
  /** Optional right rail. If omitted, the layout renders single-column. */
  rail?: ReactNode;
  /**
   * PW-13 — a FIXED header for the RECORDS COLUMN: rendered above that column's scroller, inside the
   * column, so it stays put while the records scroll.
   *
   * Without it a page's own title, badges and action row live inside the scrolling records column and
   * scroll away — so on a long picklist the Post button, the document status and the breadcrumb all
   * disappear exactly when someone has scrolled far enough to want them.
   *
   * ⚠️ It must NOT be hoisted above the columns row. PW-13 first did exactly that, and a full-width
   * header pushes the rail down by its own height — the rail then starts a few centimetres below the
   * top of the page instead of running the full vertical column, which is the whole point of a rail.
   * Owner caught it on a real screen 2026-08-28.
   *
   * Omitting it reproduces today's markup byte for byte, so pages that have not opted in are untouched.
   */
  header?: ReactNode;
  /**
   * The rail owns its own width and the drag handle is not rendered (E9).
   *
   * ⚠️ **This exists because `rail={undefined}` is NOT an opt-out.** Omitting the rail takes the early-return
   * branch below, which returns a plain `w-full` div — no `-m-6` full-bleed, no `h-[calc(100%+3rem)]`, and
   * no `overflow-y-auto`. Page scrolling silently moves back to AppLayout's `<main>` and anything that
   * assumed a scroll container (sticky headers, scroll restore) breaks with no type error. So a surface that
   * wants a FIXED-width rail needs a flag, not an omission.
   *
   * The activity panel needs one because its rail is 40px collapsed — below `minRailWidth`, so the drag
   * clamp could not express it — and because the panel is a fixed 364px by design rather than a preference.
   */
  selfSizedRail?: boolean;
  /** localStorage key for the persisted rail width. */
  storageKey?: string;
  defaultRailWidth?: number;
  minRailWidth?: number;
  maxRailWidth?: number;
  className?: string;
}

const DEFAULT_STORAGE_KEY = 'dop:detail-rail-width';

/**
 * Two-column page layout with a resizable right rail. The rail is hidden on
 * viewports narrower than the `lg` breakpoint (1024px) — at that size we
 * prefer a single column over awkward squeezing.
 */
export function DetailPageLayout({
  children,
  rail,
  header,
  selfSizedRail = false,
  storageKey = DEFAULT_STORAGE_KEY,
  defaultRailWidth = 384,
  minRailWidth = 280,
  maxRailWidth = 560,
  className,
}: DetailPageLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [railWidth, setRailWidth] = useState<number>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (Number.isFinite(parsed) && parsed >= minRailWidth && parsed <= maxRailWidth) {
        return parsed;
      }
    }
    return defaultRailWidth;
  });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const proposed = rect.right - e.clientX;
      const clamped = Math.min(maxRailWidth, Math.max(minRailWidth, proposed));
      setRailWidth(clamped);
    };

    const onUp = () => {
      setIsDragging(false);
    };

    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, minRailWidth, maxRailWidth]);

  // Persist on width change (after drag stops).
  useEffect(() => {
    if (isDragging) return;
    // A self-sized rail has no width of its own to remember, and writing here would stamp this layout's
    // DEFAULT over whatever a resizable consumer had saved — the key is shared by every detail page.
    if (selfSizedRail) return;
    try {
      window.localStorage.setItem(storageKey, String(railWidth));
    } catch {
      // Quota exceeded or storage disabled — non-fatal; width simply won't persist.
    }
  }, [isDragging, railWidth, storageKey, selfSizedRail]);

  if (!rail) {
    return (
      <div className={cn('w-full', className)}>
        {header}
        {children}
      </div>
    );
  }

  return (
    // Full-bleed: -m-6 cancels AppLayout's <main className="... p-6">, so the
    // rail goes flush against the viewport edge and the divider extends top
    // to bottom. h-[calc(100%+3rem)] adds back the 24px of padding on each
    // axis we just cancelled, so the layout fills the main exactly.
    //
    // overflow-hidden on the wrapper kills the global page scroll — the two
    // children below own their own overflow-y-auto so reviewing the picklist
    // and cross-referencing the activity rail can scroll independently.
    <div
      ref={containerRef}
      className={cn('-m-6 flex h-[calc(100%+3rem)] overflow-hidden', className)}
    >
      {/* The records column. A header belongs HERE — above this column's scroller but inside the
          column — so it stays put while the records scroll AND the rail beside it keeps the full
          height of the page. */}
      {header ? (
        <div className="flex min-w-0 flex-1 flex-col">
          {/* `shrink-0` so a tall header is never compressed by the scroller below fighting for space. */}
          <div className="shrink-0 px-6 pt-6">{header}</div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </div>
      ) : (
        <div className="min-w-0 flex-1 overflow-y-auto">{children}</div>
      )}

      {/* Drag handle. Visible only at lg+. The handle has a small visual
          target plus a wider hover hit-area for ergonomics.
          Not rendered for a self-sized rail — there is nothing to drag, and a focusable control that does
          nothing is worse than no control. */}
      {!selfSizedRail && (
      <div
        // M8: container-keyed, not `hidden lg:flex` — the viewport does not shrink when a shell
        // panel opens, but this column does. See .shell-cq-* in globals.css.
        className="shell-cq-resize items-stretch"
        role="separator"
        aria-orientation="vertical"
      >
        <button
          type="button"
          aria-label="Resize activity panel"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          className={cn(
            'group relative w-1 cursor-ew-resize bg-transparent transition-colors',
            'hover:bg-border',
            isDragging && 'bg-muted-foreground/40',
          )}
        >
          {/* Wider invisible hit-area for easier grabbing */}
          <span className="absolute inset-y-0 -left-1.5 -right-1.5" />
        </button>
      </div>
      )}

      <aside
        className={cn(
          'shrink-0 overflow-hidden',
          selfSizedRail
            // The rail draws its own border and background and decides its own width — 40px collapsed,
            // 404px with the panel open. It also stays mounted below `lg`, where it overlays instead of
            // pushing; the resizable rail cannot do that and is hidden there.
            ? 'relative flex flex-col'
            // M8: `shell-cq-rail` replaces `hidden lg:flex lg:flex-col` — same 1024px threshold,
            // but measured against the CONTENT COLUMN rather than the viewport, so opening a shell
            // panel now actually narrows it. See .shell-cq-* in globals.css.
            : 'shell-cq-rail border-l border-border bg-card text-card-foreground',
        )}
        style={selfSizedRail ? undefined : { width: `${railWidth}px` }}
      >
        {rail}
      </aside>
    </div>
  );
}
